import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  CopyIcon,
  CheckIcon,
  TrashIcon,
  LinkIcon,
  CircleNotchIcon,
  UsersIcon,
  CalendarIcon,
  ArrowSquareOutIcon,
  PencilIcon,
} from '@phosphor-icons/react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  useShareableLinks,
  useDeleteShareableLink,
  getShareableLinkUrl,
  ShareableLink,
} from 'src/api/core/useShareableLinks';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { formatDistanceToNow } from 'date-fns';
import { EditShareableLinkModal } from './EditShareableLinkModal';

interface SharedLinksManagerProps {
  open: boolean;
  onClose: () => void;
  reportId: string;
  reportTitle: string;
  onCreateNew: () => void;
}

export function SharedLinksManager({
  open,
  onClose,
  reportId,
  reportTitle,
  onCreateNew,
}: SharedLinksManagerProps) {
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [editingLink, setEditingLink] = useState<ShareableLink | null>(null);

  const { data: linksData, isLoading } = useShareableLinks({
    resourceType: `report:${reportId}`,
    enabled: open,
  });

  const links = linksData?.shareableLinks || [];

  function handleCopyLink(link: ShareableLink) {
    const url = getShareableLinkUrl(link.token);
    navigator.clipboard.writeText(url);
    setCopiedId(link.id);
    setTimeout(() => setCopiedId(null), 2000);
  }

  function formatMetadata(metadata: Record<string, unknown>): string {
    const parts: string[] = [];
    if (metadata.year) {
      parts.push(`Year: ${metadata.year}`);
    }
    if (metadata.lineType) {
      parts.push(`Line Type: ${metadata.lineType}`);
    }
    if (
      Array.isArray(metadata.divisionIds) &&
      metadata.divisionIds.length > 0
    ) {
      parts.push(`${metadata.divisionIds.length} division(s)`);
    }
    return parts.length > 0 ? parts.join(' • ') : 'No filters';
  }

  return (
    <>
      <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <LinkIcon className="h-5 w-5" />
              Shared Links
            </DialogTitle>
            <DialogDescription>
              Manage shared links for &quot;{reportTitle}&quot;
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            {isLoading && (
              <div className="flex items-center justify-center py-8">
                <CircleNotchIcon className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            )}

            {!isLoading && links.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                <LinkIcon className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No shared links yet</p>
                <p className="text-sm mt-1">
                  Create a link to share this report with others.
                </p>
              </div>
            )}

            {!isLoading &&
              links.map((link) => (
                <SharedLinkCard
                  key={link.id}
                  link={link}
                  onCopy={() => handleCopyLink(link)}
                  onEdit={() => setEditingLink(link)}
                  onDelete={() => setDeleteId(link.id)}
                  isCopied={copiedId === link.id}
                  formatMetadata={formatMetadata}
                />
              ))}
          </div>

          <DialogFooter className="flex-row justify-between sm:justify-between">
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
            <Button onClick={onCreateNew}>Create New Link</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {deleteId && (
        <DeleteLinkDialog linkId={deleteId} onClose={() => setDeleteId(null)} />
      )}

      {editingLink && (
        <EditShareableLinkModal
          open={!!editingLink}
          onClose={() => setEditingLink(null)}
          link={editingLink}
        />
      )}
    </>
  );
}

interface SharedLinkCardProps {
  link: ShareableLink;
  onCopy: () => void;
  onEdit: () => void;
  onDelete: () => void;
  isCopied: boolean;
  formatMetadata: (metadata: Record<string, unknown>) => string;
}

function SharedLinkCard({
  link,
  onCopy,
  onEdit,
  onDelete,
  isCopied,
  formatMetadata,
}: SharedLinkCardProps) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0 space-y-2">
            {/* Link Name */}
            <div className="flex items-center gap-2">
              <LinkIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              {link.name ? (
                <span className="font-medium text-sm">{link.name}</span>
              ) : (
                <span className="text-sm text-muted-foreground italic">
                  Unnamed
                </span>
              )}
            </div>

            {/* Emails */}
            <div className="flex items-center gap-2">
              <UsersIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <div className="flex flex-wrap gap-1">
                {link.allowedEmails.slice(0, 3).map((email) => (
                  <Badge key={email} variant="secondary" className="text-xs">
                    {email}
                  </Badge>
                ))}
                {link.allowedEmails.length > 3 && (
                  <Badge variant="outline" className="text-xs">
                    +{link.allowedEmails.length - 3} more
                  </Badge>
                )}
              </div>
            </div>

            {/* Metadata/Filters */}
            <div className="text-sm text-muted-foreground">
              {formatMetadata(link.metadata)}
            </div>

            {/* Created date */}
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <CalendarIcon className="h-3 w-3" />
              Created{' '}
              {formatDistanceToNow(new Date(link.createdAt), {
                addSuffix: true,
              })}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1 flex-shrink-0">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => {
                      const url = getShareableLinkUrl(link.token);
                      window.open(url, '_blank');
                    }}
                  >
                    <ArrowSquareOutIcon className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Open link</TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={onCopy}
                  >
                    {isCopied ? (
                      <CheckIcon className="h-4 w-4 text-green-600" />
                    ) : (
                      <CopyIcon className="h-4 w-4" />
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {isCopied ? 'Copied!' : 'Copy link'}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={onEdit}
                  >
                    <PencilIcon className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Edit link</TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-destructive hover:text-destructive"
                    onClick={onDelete}
                  >
                    <TrashIcon className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Delete link</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

interface DeleteLinkDialogProps {
  linkId: string;
  onClose: () => void;
}

function DeleteLinkDialog({ linkId, onClose }: DeleteLinkDialogProps) {
  const deleteLink = useDeleteShareableLink(linkId);

  async function handleDelete() {
    await deleteLink.mutateAsync(undefined as unknown as void);
    onClose();
  }

  return (
    <AlertDialog open onOpenChange={(open) => !open && onClose()}>
      <AlertDialogContent className="z-[1500]">
        <AlertDialogHeader>
          <AlertDialogTitle>Delete shared link?</AlertDialogTitle>
          <AlertDialogDescription>
            This will permanently delete this shared link. Anyone who has this
            link will no longer be able to access the report.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onClose}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleDelete}
            className="bg-destructive hover:bg-destructive/90"
            disabled={deleteLink.isPending}
          >
            {deleteLink.isPending && (
              <CircleNotchIcon className="mr-2 h-4 w-4 animate-spin" />
            )}
            Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
